"use client";

import { useAppSelector } from "@/store/hooks";
import { DefaultBackground } from "@/ui";
import { Apple, Store } from "lucide-react";

export interface AppDownloadBannerProps {
  size?: "sm" | "md" | "lg";
  className?: string;
  decorationStyle?: "prototype" | "highlight";
  withBackground?: boolean;
  onAppStoreClick?: () => void;
  onPlayStoreClick?: () => void;
}

const sizeToMinHeight: Record<
  NonNullable<AppDownloadBannerProps["size"]>,
  string
> = {
  sm: "min-h-[300px]",
  md: "min-h-[440px]",
  lg: "min-h-[480px]",
};

export default function AppDownloadBanner({
  size = "md",
  className = "",
  decorationStyle = "prototype",
  withBackground = true,
  onAppStoreClick,
  onPlayStoreClick,
}: AppDownloadBannerProps) {
  const appDownload = useAppSelector(
    (state) => state.content.data?.appDownload,
  );
  const loading = useAppSelector((state) => state.content.loading);
  const minH = sizeToMinHeight[size];

  const decorativeElements = [
    {
      size: "md" as const,
      position: { top: "16%", right: "6%" },
      opacity: 0.1,
    },
    {
      size: "sm" as const,
      position: { top: "32%", left: "8%" },
      opacity: 0.15,
    },
    {
      size: "lg" as const,
      position: { bottom: "18%", right: "10%" },
      opacity: 0.12,
    },
  ];

  // Loading state
  // if (loading || !appDownload) {
  //   return (
  //     <section className={`max-w-7xl mx-auto px-6 pb-6 ${className}`}>
  //       <div className={`relative overflow-hidden rounded-md ${minH}`}>
  //         <div className="absolute inset-0 bg-white/20 border border-white/30 backdrop-blur-sm" />
  //         <div className="relative p-8">
  //           <div className="animate-pulse space-y-4">
  //             <div className="h-8 bg-black/10 rounded w-2/3" />
  //             <div className="h-4 bg-black/10 rounded w-full" />
  //             <div className="flex gap-3">
  //               <div className="h-12 bg-black/10 rounded w-32" />
  //               <div className="h-12 bg-black/10 rounded w-32" />
  //             </div>
  //           </div>
  //         </div>
  //       </div>
  //     </section>
  //   );
  // }

  const Section = (
    <section className={`max-w-7xl mx-auto px-6 pb-6 ${className}`}>
      <div className={`relative overflow-hidden rounded-md ${minH}`}>
        {/* Glass background layer */}
        <div className="absolute inset-0 bg-white/20 border border-white/30 backdrop-blur-sm" />
        {/* Angled overlay */}
        <div className="absolute inset-0 [clip-path:polygon(0_0,100%_0,100%_70%,0_100%)] bg-black/5" />

        {/* Content grid */}
        <div className="relative grid grid-cols-1 md:grid-cols-3 gap-8 p-8 items-center">
          {/* Copy + CTAs */}
          <div className="md:col-span-2 order-2 md:order-1">
            <h2 className="text-3xl font-bold mb-2 text-black">
              {appDownload?.title}
            </h2>
            <p className="text-black/70 mb-5 max-w-xl">
              {appDownload?.subtitle}
            </p>
            <div className="flex flex-wrap gap-3">
              <a
                href={appDownload?.appStoreHref}
                onClick={onAppStoreClick}
                className="inline-flex items-center gap-2 bg-black text-white px-5 py-3 rounded-md font-medium hover:bg-black/90 transition-colors"
              >
                <Apple className="w-5 h-5" />
                <span>{appDownload?.appStoreLabel}</span>
              </a>
              <a
                href={appDownload?.playStoreHref}
                onClick={onPlayStoreClick}
                className="inline-flex items-center gap-2 bg-white/70 text-black px-5 py-3 rounded-md font-medium hover:bg-white transition-colors"
              >
                <Store className="w-5 h-5" />
                <span>{appDownload?.playStoreLabel}</span>
              </a>
            </div>
          </div>

          {/* Image tile with subtle decorations */}
          <div className="order-1 md:order-2 flex justify-center">
            <div className="relative w-64 h-64">
              <div
                className="absolute inset-0 rounded-xl bg-center bg-cover shadow-xl"
                style={{ backgroundImage: `url(${appDownload?.imageSrc})` }}
                aria-label={appDownload?.imageAlt}
                role="img"
              />
              {decorationStyle === "prototype" ? (
                <>
                  {/* Prototype-approved: solid subtle circles */}
                  <div className="pointer-events-none absolute -top-3 -left-3 w-12 h-12 rounded-full bg-black/10 z-20" />
                  <div className="pointer-events-none absolute -bottom-4 -right-4 w-20 h-20 rounded-full bg-black/10 z-20" />
                </>
              ) : (
                <>
                  {/* Highlight-style soft gradient circles */}
                  <div
                    className="pointer-events-none absolute -top-4 -left-4 w-14 h-14 rounded-full z-10 backdrop-blur-md shadow-[0_10px_20px_rgba(0,0,0,0.08)]"
                    style={{
                      background:
                        "radial-gradient(circle at 30% 30%, rgba(255,255,255,0.9), rgba(255,255,255,0.55) 60%, rgba(255,255,255,0) 100%)",
                    }}
                  />
                  <div
                    className="pointer-events-none absolute -bottom-6 -right-6 w-28 h-28 rounded-full z-10 backdrop-blur-md shadow-[0_10px_25px_rgba(0,0,0,0.12)]"
                    style={{
                      background:
                        "radial-gradient(circle at 40% 40%, rgba(255,255,255,0.9), rgba(255,255,255,0.45) 60%, rgba(255,255,255,0) 100%)",
                    }}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );

  return withBackground ? (
    <DefaultBackground
      decorativeElements={decorativeElements}
      positionMode="absolute"
      fullHeight={false}
    >
      {Section}
    </DefaultBackground>
  ) : (
    Section
  );
}
